import { S, TRI_R, edgesFor, rotXZ, pointInFootprint, type FootprintShape } from './grid';
import { BUILD } from '../../config';
import { isWallSlot } from './catalog';
import type { PlacedPiece } from './types';

// ROOM GRAPH: exact enclosed-room detection over the placed pieces, replacing the 16-ray isEnclosed
// heuristic. Built from the same data placement maintains -- footprint pieces are the walkable CELLS
// of a storey, wall pieces cover the EDGES between them as height intervals, footprints one wall
// height (or a half wall) up are the LIDS. A room is a connected component of cells; it is SEALED
// when every perimeter edge carries wall coverage from the floor to the lid (doorways/windows/doors
// count -- they are the intended holes in an enclosure) and every cell is lidded. Rebuilt only when
// the build graph changes (commit/remove flip a dirty flag in the Builder), never per frame.
//
// Consumers: interior-view privacy (only a SEALED room hides its contents), item size classes
// (clearance = lid height - floor), and the opt-in room debug overlay.

export interface Room {
  liddedCount: number;        // how many of the cells have a roof/floor over them (drives the INCREMENTAL dim)
  perimEdges: number;         // perimeter edge slots checked during the seal pass
  perimCovered: number;       // ...of which carry a wall/doorway/window (openings still count as enclosure)
  id: number;
  storeyTop: number;          // the floor height (footprint top) this room stands on
  cellCount: number;          // how many footprint cells make up the room
  sealed: boolean;            // fully covered perimeter + fully lidded
  lidHeight: number | null;   // lowest lid over the room's cells (clearance = lidHeight - storeyTop), null = open sky somewhere
  openings: number;           // doorway/window edges on the perimeter (how the room breathes)
}

// Per-cell readout for the debug room overlay (tint at floor height, sealed green / leaky orange).
export interface RoomDebugCell { x: number; z: number; top: number; shape: FootprintShape; yaw: number; sealed: boolean; }

interface CellRec {
  x: number; z: number;       // footprint centre
  top: number;                // walkable floor height
  shape: FootprintShape;
  yaw: number;
  room: number;               // room id after flood fill (-1 = unassigned)
  lid: number | null;         // lowest footprint top a wall (or half wall) band above, null = sky
}

// A wall piece's vertical cover on one edge column. open = doorway/window: still seals the room,
// but breathes (counted as an opening). Solid walls and half walls are open=false.
interface EdgeSeg { base: number; top: number; open: boolean; }

const EPS = 0.35;       // lid height-band tolerance (matches the ceiling rules' +-0.3)
const KQ = 4;           // edge-midpoint key quantization: 0.25m, same spirit as grid.keyOf
const WALK_H = 0.75;    // adjacency probe height above the floor: any wall band covering it blocks the edge
const COVER_TOL = 0.2;  // slack when merging wall intervals over floor..lid (pieces meet within ~0.1)

function q(v: number): number { return Math.round(v * KQ); }
function xzKey(x: number, z: number): string { return `${q(x)},${q(z)}`; }
// Bucket key for the cell spatial hash (numeric, so per-frame roomAt queries allocate nothing).
function bucketKey(x: number, z: number): number { return (Math.floor(x / S) + 2048) * 4096 + (Math.floor(z / S) + 2048); }

// Any wall band covering the walk height above this floor blocks cell adjacency (rooms never merge
// through a wall, a half wall, a doorway or a window -- doorways divide rooms on purpose).
function blockedAt(segs: EdgeSeg[] | undefined, floorTop: number): boolean {
  if (!segs) return false;
  for (const s of segs) if (s.base <= floorTop + WALK_H && s.top >= floorTop + WALK_H) return true;
  return false;
}

// Is the blocking band at this floor an opening kind (doorway/window)?
function openAt(segs: EdgeSeg[], floorTop: number): boolean {
  for (const s of segs) if (s.base <= floorTop + WALK_H && s.top >= floorTop + WALK_H && s.open) return true;
  return false;
}

// Do the wall intervals on an edge column cover [from, to] (floor to lid)? Interval merge with a
// small tolerance: a lone half wall under a full-height lid leaves a gap and fails, two stacked
// half walls (or one full wall) pass.
function covers(segs: EdgeSeg[], from: number, to: number): boolean {
  let cur = from;
  for (let guard = 0; guard < 8; guard++) {
    if (cur >= to - COVER_TOL) return true;
    let next = cur;
    for (const s of segs) if (s.base <= cur + COVER_TOL && s.top > next) next = s.top;
    if (next === cur) return false; // hole: nothing continues the coverage
    cur = next;
  }
  return cur >= to - COVER_TOL;
}

export class RoomGraph {
  private rooms: Room[] = [];
  private cells: CellRec[] = [];
  private buckets = new Map<number, CellRec[]>();

  // Rebuild from the full placed-piece list (the Builder calls this lazily after commit/removePiece
  // batches, at most once per frame). Cost is O(pieces + cells x edges); a few hundred pieces
  // rebuild in well under a millisecond.
  rebuild(pieces: Iterable<PlacedPiece>): void {
    const cells: CellRec[] = [];
    const edges = new Map<string, EdgeSeg[]>();
    for (const p of pieces) {
      if (p.family !== 'none' && p.top !== undefined) {
        // Every footprint is BOTH a floor cell and a potential lid for the storey below.
        cells.push({ x: p.position.x, z: p.position.z, top: p.top, shape: p.family as FootprintShape, yaw: p.yaw, room: -1, lid: null });
      } else if (p.family === 'none' && p.base !== undefined && p.top !== undefined && isWallSlot(p.defId)) {
        // Wall-slot pieces (wall / half_wall / doorway / double_doorway / window). Walls are placed
        // inset wallThick/2 from the tile edge along their outward normal (updateWall), so push the
        // midpoint back OUT to land on the exact edge the cells derive from their own perimeters.
        const nx = Math.sin(p.yaw), nz = Math.cos(p.yaw); // wall outward normal (yaw stored as angle(n))
        const k = xzKey(p.position.x + nx * (BUILD.wallThick / 2), p.position.z + nz * (BUILD.wallThick / 2));
        const seg: EdgeSeg = { base: p.base, top: p.top, open: p.defId === 'doorway' || p.defId === 'double_doorway' || p.defId === 'window' };
        const list = edges.get(k);
        if (list) list.push(seg); else edges.set(k, [seg]);
      }
    }
    this.cells = cells;

    // Spatial hash over cell footprints: each cell registers in every S-sized bucket its
    // circumradius touches, so a point query only reads its own bucket.
    this.buckets = new Map();
    for (const c of cells) {
      const r = c.shape === 'square' ? S * 0.7071068 : TRI_R;
      for (let ix = Math.floor((c.x - r) / S); ix <= Math.floor((c.x + r) / S); ix++) {
        for (let iz = Math.floor((c.z - r) / S); iz <= Math.floor((c.z + r) / S); iz++) {
          const k = (ix + 2048) * 4096 + (iz + 2048);
          const list = this.buckets.get(k);
          if (list) list.push(c); else this.buckets.set(k, [c]);
        }
      }
    }

    // Lids: a footprint whose centre coincides (stacking is exact, so 0.5m is generous) and whose
    // top sits one wall height -- or a half wall -- above the cell's floor covers it.
    for (const c of cells) {
      for (const l of this.buckets.get(bucketKey(c.x, c.z)) ?? []) {
        if (l === c) continue;
        if ((l.x - c.x) ** 2 + (l.z - c.z) ** 2 > 0.25) continue;
        const rel = l.top - c.top;
        if (Math.abs(rel - BUILD.wallHeight) > EPS && Math.abs(rel - BUILD.wallHeight / 2) > EPS) continue;
        c.lid = c.lid === null ? l.top : Math.min(c.lid, l.top);
      }
    }

    // Cell adjacency goes through shared edge midpoints at the same storey band: two cells whose
    // perimeters produce the same key are neighbors (mate() lands adjacent pieces exactly, so the
    // quantized keys agree even on free-placed float positions).
    const byEdge = new Map<string, CellRec[]>();
    for (const c of cells) {
      for (const e of edgesFor(c.shape)) {
        const m = rotXZ(e.mx, e.mz, c.yaw);
        const k = `${xzKey(c.x + m.x, c.z + m.z)}@${Math.round(c.top * 2)}`;
        const list = byEdge.get(k);
        if (list) list.push(c); else byEdge.set(k, [c]);
      }
    }

    // Flood fill: connected components across edges no wall band blocks.
    this.rooms = [];
    let nextId = 0;
    const queue: CellRec[] = [];
    for (const seed of cells) {
      if (seed.room >= 0) continue;
      const id = nextId++;
      seed.room = id;
      queue.length = 0;
      queue.push(seed);
      let count = 0;
      while (queue.length) {
        const c = queue.pop()!;
        count++;
        for (const e of edgesFor(c.shape)) {
          const m = rotXZ(e.mx, e.mz, c.yaw);
          const mx = c.x + m.x, mz = c.z + m.z;
          if (blockedAt(edges.get(xzKey(mx, mz)), c.top)) continue;
          const nb = byEdge.get(`${xzKey(mx, mz)}@${Math.round(c.top * 2)}`);
          if (!nb) continue;
          for (const n of nb) if (n.room < 0) { n.room = id; queue.push(n); }
        }
      }
      this.rooms.push({ id, storeyTop: seed.top, cellCount: count, sealed: false, lidHeight: null, openings: 0, liddedCount: 0, perimEdges: 0, perimCovered: 0 });
    }

    // Sealing pass. An edge with a wall band is an INTERIOR partition when a same-room cell sits
    // across it (connected around, e.g. a half-wall divider inside one room) and is exempt; a
    // PERIMETER edge must carry coverage from the floor all the way to the cell's lid, so a lone
    // half wall under a full-height lid leaks. A bare edge with no neighbor cell leaks outright.
    const roomSealed: boolean[] = this.rooms.map(() => true);
    const roomLidded: boolean[] = this.rooms.map(() => true);
    const roomLid: number[] = this.rooms.map(() => Infinity);
    for (const c of cells) {
      const ri = c.room;
      const room = this.rooms[ri];
      if (c.lid === null) roomLidded[ri] = false;
      else { roomLid[ri] = Math.min(roomLid[ri], c.lid); room.liddedCount++; }
      for (const e of edgesFor(c.shape)) {
        const m = rotXZ(e.mx, e.mz, c.yaw);
        const mx = c.x + m.x, mz = c.z + m.z;
        const segs = edges.get(xzKey(mx, mz));
        const nb = byEdge.get(`${xzKey(mx, mz)}@${Math.round(c.top * 2)}`);
        if (!blockedAt(segs, c.top)) {
          // No wall: the edge must lead to another cell (the fill linked it into this room).
          if (!nb || nb.length < 2) { roomSealed[ri] = false; room.perimEdges++; } // a BARE perimeter edge: counts, uncovered
          continue;
        }
        let interior = false;
        if (nb) for (const n of nb) if (n !== c && n.room === ri) { interior = true; break; }
        if (interior) continue;
        room.perimEdges++;
        room.perimCovered++; // walls, doorways and windows all count as enclosure (openings are intended holes)
        if (openAt(segs!, c.top)) this.rooms[ri].openings++;
        if (c.lid !== null && !covers(segs!, c.top, c.lid)) roomSealed[ri] = false;
      }
    }
    for (const r of this.rooms) {
      r.sealed = roomSealed[r.id] && roomLidded[r.id];
      r.lidHeight = roomLidded[r.id] ? roomLid[r.id] : null;
    }
  }

  // The room at world (x,z) for the storey at or under height y: exact point-in-footprint over the
  // bucketed cells, picking the HIGHEST floor that is not above the query point (feet or a floor
  // hit both land essentially ON their storey's top). Null outside every footprint.
  roomAt(x: number, z: number, y: number): Room | null {
    const list = this.buckets.get(bucketKey(x, z));
    if (!list) return null;
    let best: CellRec | null = null;
    for (const c of list) {
      if (c.top > y + 0.5) continue;
      if (best !== null && c.top <= best.top) continue;
      if (!pointInFootprint(c.shape, x, z, c.x, c.z, c.yaw)) continue;
      best = c;
    }
    return best === null ? null : this.rooms[best.room] ?? null;
  }

  // Vertical clearance of the room at (x,z,y): lid minus floor, or Infinity under open sky /
  // outside any room. Drives the deployable size classes (a locker needs a full-height room).
  clearanceAt(x: number, z: number, y: number): number {
    const r = this.roomAt(x, z, y);
    if (!r || r.lidHeight === null) return Infinity;
    return r.lidHeight - r.storeyTop;
  }

  // Is (x,z,y) inside a SEALED room? Drives the interior privacy view.
  isSealedAt(x: number, z: number, y: number): boolean {
    return this.roomAt(x, z, y)?.sealed ?? false;
  }

  // INCREMENTAL enclosure 0..1 at (x,z,y): roof fraction x wall fraction of the room you stand in.
  // A 2x2 walled room with 1 of 4 ceilings = 0.25 (the owner's spec); fully sealed = 1; a roofed
  // slab with no walls, or open ground, = 0 (the landscape never dims without real enclosure).
  enclosureAt(x: number, z: number, y: number): number {
    const r = this.roomAt(x, z, y);
    if (!r) return 0;
    if (r.sealed) return 1;
    const lidFrac = r.cellCount > 0 ? r.liddedCount / r.cellCount : 0;
    const wallFrac = r.perimEdges > 0 ? r.perimCovered / r.perimEdges : 0;
    return Math.max(0, Math.min(1, lidFrac * wallFrac));
  }

  all(): readonly Room[] { return this.rooms; }

  // Per-cell readout for the debug room overlay. Allocates; debug-only, placement-event rate.
  debugCells(): RoomDebugCell[] {
    return this.cells.map((c) => ({ x: c.x, z: c.z, top: c.top, shape: c.shape, yaw: c.yaw, sealed: this.rooms[c.room]?.sealed ?? false }));
  }
}
